import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Typography, message, Space, Modal } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import VrukshaEduLogo from '../components/VrukshaEduLogo';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';
import './Login.css';

const { Title, Text, Paragraph } = Typography;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [resetVisible, setResetVisible] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [form] = Form.useForm();
  const { login, user, adminUser } = useAuth();
  const navigate = useNavigate();

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      await login(values.email, values.password);
      message.success('Login successful!');
      // live_test_viewer goes directly to live tests
      navigate(adminUser?.role === 'live_test_viewer' ? '/live-tests' : '/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      message.error(error.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If already logged in, skip login screen
  useEffect(() => {
    if (user) {
      navigate(adminUser?.role === 'live_test_viewer' ? '/live-tests' : '/dashboard');
    }
  }, [user, adminUser, navigate]);

  const openResetModal = () => {
    const currentEmail = form.getFieldValue('email');
    setResetEmail(currentEmail || '');
    setResetVisible(true);
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      message.warning('Please enter your email to reset your password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      message.success('Password reset email sent. Please check your inbox.');
      setResetVisible(false);
    } catch (error: any) {
      console.error('Reset password error:', error);
      message.error(error.message || 'Failed to send reset email.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
      </div>
      
      <Card className="login-card" bordered={false}>
        <div className="login-header">
          <div className="logo-container">
            <VrukshaEduLogo width={80} height={80} />
          </div>
          <Title level={2} className="login-title">NammaPrep Admin Portal</Title>
          <Paragraph className="login-subtitle">
            Standardized Exam & Academic Management
          </Paragraph>
        </div>

        <Form
          name="login"
          form={form}
          onFinish={onFinish}
          layout="vertical"
          size="large"
          className="login-form"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input
              prefix={<UserOutlined className="input-icon" />}
              placeholder="Email Address"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="Password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              icon={<LoginOutlined />}
              className="login-button"
            >
              Sign In
            </Button>
            <Button type="link" block onClick={openResetModal} className="forgot-password-link">
              Forgot Password?
            </Button>
          </Form.Item>
        </Form>

        <div className="login-footer">
          <Space direction="vertical" align="center" size="small" style={{ width: '100%' }}>
            <Text type="secondary" className="footer-text">
              Powered by Calloc Technologies Pvt Ltd
            </Text>
            <VrukshaEduLogo width={36} height={36} />
            <Text type="secondary" className="footer-version">
              Version 1.0.0 • Secure & Reliable
            </Text>
          </Space>
        </div>
      </Card>

      <Modal
        title="Reset Password"
        open={resetVisible}
        onOk={handleResetPassword}
        onCancel={() => setResetVisible(false)}
        okText="Send Reset Email"
      >
        <Typography.Paragraph>
          Enter your account email to receive a password reset link.
        </Typography.Paragraph>
        <Input
          placeholder="Email Address"
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
        />
      </Modal>


    </div>
  );
};

export default Login;
